import http from '@/utils/http'
import { ElMessage, ElLoading } from 'element-plus'
import { logError } from '@/utils/logger'

const initialState = () => ({
  item: {},
  originalItem: {},
  isLoading: false,
  isReadOnly: false
})

export const state = initialState()

export const mutations = {
  RESET (state) {
    const newState = initialState()
    Object.keys(newState).forEach(key => {
      state[key] = newState[key]
    })
  },
  SET_ITEM (state, item) {
    state.item = { ...item }
    state.originalItem = { ...item }
  },
  SET_ITEM_PROP (state, { prop, value }) {
    state.item = {
      ...state.item,
      [prop]: value
    }
  },
  SET_IS_LOADING (state, isLoading) {
    state.isLoading = isLoading
  },
  SET_IS_READ_ONLY (state, isReadOnly = false) {
    state.isReadOnly = isReadOnly
  }
}

export const getters = {
  item: state => state.item,
  originalItem: state => state.originalItem,
  isLoading: state => state.isLoading,
  isReadOnly: state => state.isReadOnly
}

export const actions = {
  fetchJob ({ commit, getters, rootGetters }, { id = null, force = false } = {}) {
    if (id) {
      if (!force && id === getters.item.ID) {
        return getters.item
      }

      return http({
        url: `${process.env.VUE_APP_API_URL}job/${rootGetters['auth/token']}/${id}`
      }).then(data => {
        commit('SET_ITEM', data)
        return data
      }).catch(e => {
        ElMessage.error('error loading job')

        throw e
      })
    } else {
      commit('SET_ITEM', {})
    }
  },
  saveJob ({ state, commit, dispatch, rootGetters }, job) {
    commit('SET_IS_LOADING', true)

    const item = job || { ...state.item }
    const isNewItem = !item.ID

    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}job/${rootGetters['auth/token']}`,
      data: item
    }).then(async data => {
      if (!data.ID) {
        throw new Error('Error saving job: no ID')
      }

      // seems to be unnecessary now
      // if (isNewItem) {
      //   http({
      //     url: `${process.env.VUE_APP_API_URL}subscription/addjob/${rootGetters['auth/token}/${item.SubscriptionID}/${data.ID}`
      //   })
      // }

      let reloadParams = false
      const results = []
      if (isNewItem) {
        results.push(await dispatch('jobParameters/saveDefaultParams', { param: item, system: 'Source', id: data.ID }, { root: true }))
        results.push(await dispatch('jobParameters/saveDefaultParams', { param: item, system: 'Destination', id: data.ID }, { root: true }))
      } else {
        if (item.DataConnectorPluginSource !== state.originalItem.DataConnectorPluginSource) {
          results.push(await dispatch('jobParameters/removeSavedParams', { param: item, system: 'Source' }, { root: true }))

          results.push(await dispatch('jobParameters/saveDefaultParams', { param: item, system: 'Source', id: data.ID }, { root: true }))

          reloadParams = true
        }

        if (item.DataConnectorPluginDestination !== state.originalItem.DataConnectorPluginDestination) {
          results.push(await dispatch('jobParameters/removeSavedParams', { param: item, system: 'Destination' }, { root: true }))

          results.push(await dispatch('jobParameters/saveDefaultParams', { param: item, system: 'Destination', id: data.ID }, { root: true }))

          reloadParams = true
        }
      }

      let failedParamSaving = false
      for (const res of results) {
        if (!res) {
          failedParamSaving = true
          break
        }
      }

      if (failedParamSaving) {
        ElMessage.warning('job was saved but not all job parameters were saved')
      } else {
        ElMessage.success(isNewItem ? 'job created' : 'job saved')
      }

      if (reloadParams) {
        await dispatch('jobParameters/fetchParameters', { jobId: data.ID, force: true }, { root: true })
      }

      commit('SET_ITEM', data)
      commit('SET_IS_LOADING', false)

      return data
    }).catch(e => {
      commit('SET_IS_LOADING', false)

      ElMessage.error('error loading job')

      throw e
    })
  },
  saveJobSchedule ({ state, rootGetters }, jobSchedule) {
    // no logic but it has to be so
    jobSchedule.Id = jobSchedule.ID

    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}job/saveschedule/${rootGetters['auth/token']}`,
      data: jobSchedule
    }).then(data => {
      if (!data) {
        ElMessage.error('Error saving job schedule')
        return false
      }

      ElMessage.success('job schedule saved')

      return true
    }).catch(e => {
      ElMessage.error('Error saving job schedule')
      logError(e, { jobSchedule }, 'job: error saving job schedule')

      return false
    })
  },
  startJob ({ state, rootGetters }, job) {
    const loadingInstance = ElLoading.service()

    return http({
      url: `${process.env.VUE_APP_API_URL}job/launch/${rootGetters['auth/token']}/${job.ID}`
    })
      .then(response => {
        if (response === 'job started.') {
          ElMessage.success(response)
        } else if (!response) {
          ElMessage.info('No server available, try again later.')
        } else {
          ElMessage.warning(response)
        }

        loadingInstance.close()
      })
      .catch(() => {
        ElMessage.error('Error starting job')
        loadingInstance.close()
      })
  },
  updateCounters ({ rootGetters, commit }, jobId) {
    return http({
      url: `${process.env.VUE_APP_API_URL}job/updatecounters/${rootGetters['auth/token']}/${jobId}`,
      convertJson: false
    })
  },
  exportCsv ({ rootGetters }, { job, filter }) {
    return http({
      url: `${process.env.VUE_APP_API_URL}DAS/data/retrieve/csv/${rootGetters['auth/token']}`,
      method: 'post',
      data: filter,
      convertJson: false
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = 'RecordData.csv'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
      })
  }
}
